// LJN — Le Journey Halong · Brand Design Tokens
// Single source of truth. Import này vào mọi component.

export const colors = {
  // Brand primary — LJN Teal (background sections, CTA, key UI)
  teal:        '#0c5776',
  tealLight:   '#1F6F5F',  // hover state
  tealMid:     'rgba(10,87,118,0.08)',  // subtle tint trên nền sáng
  tealDivider: 'rgba(10,87,118,0.15)', // divider, border

  // Dark surface (hero overlay, footer)
  midnight:    '#071E30',
  navy:        '#0B3250',  // giữ lại để dùng trong hero gradient

  // Background
  ivory:       '#F5F2EC',
  offWhite:    '#FAFAF8',

  // Accent — dùng tiết kiệm: icon nhỏ, badge, star rating
  gold:        '#C9A56A',

  // Text
  charcoal:    '#1A1A2E',
  slate:       '#5A6475',

  // Utilities
  dark:        '#000000',
  white:       '#FFFFFF',
  divider:     'rgba(10,87,118,0.12)',
  shadow:      '0 2px 12px rgba(10,87,118,0.08)',
  heroOverlay: 'rgba(7,30,48,0.35)',
}

export const fonts = {
  serif: "'Cormorant Garamond', Georgia, serif",
  sans:  "'Montserrat', system-ui, sans-serif",
}

export const radius = {
  button: '0px',
  card:   '0px',
}

// Google Fonts import string — paste vào @import hoặc <link>
export const googleFontsUrl =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Montserrat:wght@300;400;500&display=swap'

// CSS custom properties — inject vào :root qua brandCSS
export const cssVars = `
  --color-teal:         ${colors.teal};
  --color-teal-light:   ${colors.tealLight};
  --color-teal-mid:     ${colors.tealMid};
  --color-teal-divider: ${colors.tealDivider};
  --color-navy:         ${colors.navy};
  --color-midnight:     ${colors.midnight};
  --color-ivory:        ${colors.ivory};
  --color-off-white:    ${colors.offWhite};
  --color-gold:         ${colors.gold};
  --color-charcoal:     ${colors.charcoal};
  --color-slate:        ${colors.slate};
  --color-dark:         ${colors.dark};
  --color-white:        ${colors.white};
  --color-divider:      ${colors.divider};
  --color-shadow:       ${colors.shadow};
  --font-serif:         ${fonts.serif};
  --font-sans:          ${fonts.sans};
  --radius-btn:         ${radius.button};
  --radius-card:        ${radius.card};
`

// Inject Google Fonts <link> vào <head> — gọi 1 lần ở main.jsx
export function injectFonts() {
  if (document.getElementById('ljn-fonts')) return
  const link = document.createElement('link')
  link.id   = 'ljn-fonts'
  link.rel  = 'stylesheet'
  link.href = googleFontsUrl
  document.head.appendChild(link)
}

// Base CSS reset + root vars — import 1 lần ở main.jsx
export const brandCSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root { ${cssVars} }

  body {
    font-family: var(--font-sans);
    font-weight: 400;
    color: var(--color-charcoal);
    background: var(--color-off-white);
    -webkit-font-smoothing: antialiased;
  }

  /* ── Shared utility classes ── */

  /* Typography */
  .ljn-display {
    font-family: var(--font-serif);
    font-weight: 300;
    line-height: 1.05;
    letter-spacing: -0.01em;
  }
  .ljn-heading {
    font-family: var(--font-serif);
    font-weight: 400;
    line-height: 1.2;
  }
  .ljn-label {
    font-family: var(--font-sans);
    font-weight: 500;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  /* Buttons — shared base */
  .ljn-btn-primary,
  .ljn-btn-secondary,
  .ljn-btn-hero {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 15px 32px;
    border-radius: var(--radius-btn);
    cursor: pointer;
    transition: background 0.22s, border-color 0.22s, gap 0.22s;
    text-decoration: none;
    white-space: nowrap;
  }
  .ljn-btn-primary,
  .ljn-btn-secondary,
  .ljn-btn-hero { border: 1px solid transparent; }

  /* Primary — teal bg, dùng trên nền sáng */
  .ljn-btn-primary {
    background: var(--color-teal);
    color: var(--color-white);
    border-color: var(--color-teal);
  }
  .ljn-btn-primary:hover { background: var(--color-teal-light); border-color: var(--color-teal-light); gap: 16px; }

  /* Secondary — gold outline, dùng trên nền sáng */
  .ljn-btn-secondary {
    background: transparent;
    color: var(--color-gold);
    border-color: var(--color-gold);
  }
  .ljn-btn-secondary:hover { background: var(--color-ivory); gap: 16px; }

  /* Hero — white outline + gold text, dùng trên hero tối */
  .ljn-btn-hero {
    background: transparent;
    color: var(--color-ivory);
    border-color: var(--color-ivory);
  }
  .ljn-btn-hero:hover {
    background: rgba(245,242,236,0.72);
    border-color: var(--color-ivory);
    gap: 16px;
  }

  /* Divider */
  .ljn-divider {
    width: 40px;
    height: 1px;
    background: var(--color-gold);
    margin: 16px 0;
  }

  /* Section heading pattern */
  .ljn-section-title {
    font-family: var(--font-serif);
    font-size: clamp(1.8rem, 3vw, 2.6rem);
    font-weight: 300;
    color: var(--color-dark);
    margin-bottom: 8px;
  }
  .ljn-section-subtitle {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--color-slate);
    margin-bottom: 48px;
  }

  /* Card base */
  .ljn-card {
    background: var(--color-white);
    border-radius: var(--radius-card);
    box-shadow: var(--color-shadow);
    overflow: hidden;
  }

  /* Badge */
  .ljn-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--color-ivory);
    color: var(--color-navy);
    font-family: var(--font-sans);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 5px 10px;
    border-radius: 2px;
  }
  .ljn-badge--gold {
    background: transparent;
    color: var(--color-gold);
    border: 1px solid var(--color-gold);
  }

`
